import { baseUrl, getTokenFromLocalStorage } from "./Configuration";

export interface LifecycleUnitSummary {
  unitId: string;
  apartmentTitle: string;
  building?: string;
  unitName?: string;
  line?: string;
  floor?: number;
  bedrooms?: number;
  currentRent?: number;
  /** False for a unit the lifecycle log has not started tracking yet. */
  hasCycle: boolean;
  isOpen: boolean;
  closedReason?: string;
  latestFlow?: string;
  latestEventType?: string;
  latestEventAtUtc?: string;
}

export interface LifecycleUnit {
  id: string;
  apartmentTitle: string;
  building?: string;
  buildingAddress?: string;
  unitName?: string;
  line?: string;
  floor?: number;
  bedrooms?: number;
  currentRent?: number;
  proposedRent?: number;
  leaseFrom?: string;
  leaseTo?: string;
}

export interface LifecycleMember {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  isGuarantor: boolean;
}

export interface LifecycleGroup {
  kind: "tenant" | "prospect";
  /** ProspectiveTenants id for an enquirer group; null for the tenancy. */
  id: string | null;
  name: string;
  members: LifecycleMember[];
}

export interface LifecycleEvent {
  id: string;
  flow: "Renewal" | "MoveOut" | "Rental";
  eventType: string;
  occurredAtUtc: string;
  /** Bumped on every merge, unlike occurredAtUtc — tells which of two events happened most recently. */
  updatedAtUtc: string;
  /** Null for events that belong to the whole cycle rather than one person. */
  prospectOrTenantId: string | null;
  detail?: Record<string, any> | null;
}

export interface LifecycleCycle {
  id: string;
  startedAtUtc: string;
  closedAtUtc?: string;
  closedReason?: string;
  workflowType?: "Mandatory" | "Optional";
  groups: LifecycleGroup[];
  events: LifecycleEvent[];
}

export interface LifecycleTimeline {
  unit: LifecycleUnit;
  /** Newest first: the unit's full lifecycle history, current cycle through its oldest predecessor. */
  cycles: LifecycleCycle[];
}

async function authedFetch(url: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
      ...(options.headers || {}),
    },
  });

  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP error! Status: ${response.status}`);
  }

  return response;
}

export async function fetchTimelineUnits(): Promise<LifecycleUnitSummary[]> {
  const response = await authedFetch(`${baseUrl}/api/event-history/units`);
  return (await response.json()) as LifecycleUnitSummary[];
}

export async function fetchTimeline(unitId: string): Promise<LifecycleTimeline> {
  const response = await authedFetch(`${baseUrl}/api/event-history/${unitId}`);
  return (await response.json()) as LifecycleTimeline;
}

export async function fetchTimelineByListing(
  listingId: string,
): Promise<LifecycleTimeline> {
  const response = await authedFetch(
    `${baseUrl}/api/event-history/by-listing/${listingId}`,
  );
  return (await response.json()) as LifecycleTimeline;
}

export async function setRentedProspect(
  cycleId: string,
  prospectOrTenantId: string | null,
): Promise<void> {
  await authedFetch(
    `${baseUrl}/api/event-history/cycles/${cycleId}/rented-prospect`,
    {
      method: "PATCH",
      body: JSON.stringify({ prospectOrTenantId }),
    },
  );
}
